import api from "./api";
import type {
  WorkLog,
  WorkLogRequest,
  WorkLogFilters,
  ManagerCommentRequest,
  DailyReportRequiredStatus,
  DailyReportSettings,
  DailyReportSettingsUpdate,
  DailyReportComplianceResponse,
  DailyReportCandidate,
} from "@/types/worklog";

export interface WorkLogListResponse {
  items: WorkLog[];
  total: number;
  page: number;
  page_size: number;
}

export const worklogService = {
  // === 업무일지 ===

  // 업무일지 목록 조회
  async getWorkLogs(params?: WorkLogFilters): Promise<WorkLogListResponse> {
    const response = await api.get<WorkLogListResponse>("/worklogs", {
      params,
    });
    return response.data;
  },

  // 조회 가능한 사용자 목록 (부서 기반 권한 필터링)
  async getViewableUsers(): Promise<{ id: string; name: string; email: string; department?: { id: number; name: string } | null }[]> {
    const response = await api.get("/worklogs/viewable-users");
    return response.data;
  },

  // 내 업무일지 목록 조회
  async getMyWorkLogs(params?: WorkLogFilters): Promise<WorkLogListResponse> {
    const response = await api.get<WorkLogListResponse>("/worklogs/my", {
      params,
    });
    return response.data;
  },

  // 오늘 업무일지 조회
  async getTodayWorkLog(): Promise<WorkLog | null> {
    const response = await api.get<WorkLog | null>("/worklogs/today");
    return response.data;
  },

  // 특정 날짜 업무일지 조회
  async getWorkLogByDate(date: string): Promise<WorkLog | null> {
    const response = await api.get<WorkLog | null>(`/worklogs/date/${date}`);
    return response.data;
  },

  // 업무일지 상세 조회
  async getWorkLog(worklogId: number): Promise<WorkLog> {
    const response = await api.get<WorkLog>(`/worklogs/${worklogId}`);
    return response.data;
  },

  // 업무일지 생성/수정 (upsert)
  async saveWorkLog(data: WorkLogRequest): Promise<WorkLog> {
    const response = await api.post<WorkLog>("/worklogs", data);
    return response.data;
  },

  // 업무일지 삭제
  async deleteWorkLog(worklogId: number): Promise<void> {
    await api.delete(`/worklogs/${worklogId}`);
  },

  // 매니저 코멘트 추가
  async addManagerComment(
    worklogId: number,
    data: ManagerCommentRequest
  ): Promise<WorkLog> {
    const response = await api.post<WorkLog>(
      `/worklogs/${worklogId}/comment`,
      data
    );
    return response.data;
  },

  // 팀 업무일지 조회 (매니저용)
  async getTeamWorkLogs(params?: WorkLogFilters): Promise<WorkLogListResponse> {
    const response = await api.get<WorkLogListResponse>("/worklogs/team", {
      params,
    });
    return response.data;
  },

  // === 일일 업무 보고 필수 등록 ===

  // 본인 필수 등록 상태 (배지·배너·차단·상태칩 공용). date 지정 시 해당 날짜 상태(date_status) 포함
  async getRequiredStatus(date?: string): Promise<DailyReportRequiredStatus> {
    const response = await api.get<DailyReportRequiredStatus>("/worklogs/required-status", {
      params: date ? { date } : undefined,
    });
    return response.data;
  },

  // 관리자 설정 조회/저장
  async getDailyReportSettings(): Promise<DailyReportSettings> {
    const response = await api.get<DailyReportSettings>("/worklogs/settings");
    return response.data;
  },

  async updateDailyReportSettings(data: DailyReportSettingsUpdate): Promise<DailyReportSettings> {
    const response = await api.put<DailyReportSettings>("/worklogs/settings", data);
    return response.data;
  },

  // 제외 직원 선택 후보 (같은 회사 재직 직원만: 고객·퇴사·대리관리 계정 제외)
  async getSettingsCandidates(): Promise<DailyReportCandidate[]> {
    const response = await api.get<DailyReportCandidate[]>("/worklogs/settings/candidates");
    return response.data;
  },

  // 팀/회사 제출 현황 (관리자·부서장)
  async getCompliance(params: {
    start_date?: string;
    end_date?: string;
    department_id?: number;
  }): Promise<DailyReportComplianceResponse> {
    const response = await api.get<DailyReportComplianceResponse>("/worklogs/compliance", {
      params,
    });
    return response.data;
  },
};


export default worklogService;
